import type { ReactElement } from "react";
import { ResponsiveContainer } from "recharts";
import { Skeleton } from "@/components/ui/skeleton.tsx";

export interface ChartTable {
	columns: string[];
	rows: string[][];
}

export interface ChartProps {
	label: string;
	height?: number;
	isPending?: boolean;
	isError?: boolean;
	isEmpty?: boolean;
	emptyMessage?: string;
	table?: ChartTable;
	children: ReactElement;
}

export function Chart({
	label,
	height = 260,
	isPending,
	isError,
	isEmpty,
	emptyMessage = "Sem dados para o período.",
	table,
	children,
}: ChartProps) {
	if (isPending) {
		return <Skeleton data-slot="chart-skeleton" style={{ height }} />;
	}

	if (isError) {
		return (
			<p data-slot="chart-error" role="status">
				Não foi possível carregar o gráfico.
			</p>
		);
	}

	if (isEmpty) {
		return (
			<p data-slot="chart-empty" role="status">
				{emptyMessage}
			</p>
		);
	}

	return (
		<>
			<div data-slot="chart" style={{ height }} role="img" aria-label={label}>
				<ResponsiveContainer width="100%" height="100%">
					{children}
				</ResponsiveContainer>
			</div>
			{table && (
				<div className="visually-hidden">
					<table>
						<caption>{label}</caption>
						<thead>
							<tr>
								{table.columns.map((column) => (
									<th key={column} scope="col">
										{column}
									</th>
								))}
							</tr>
						</thead>
						<tbody>
							{table.rows.map((row) => (
								<tr key={row.join("|")}>
									{row.map((cell, index) => (
										<td key={table.columns[index] ?? String(index)}>{cell}</td>
									))}
								</tr>
							))}
						</tbody>
					</table>
				</div>
			)}
		</>
	);
}
